import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useLanguage, useTheme } from '../App';
import { usePortalCleanup } from '../hooks/usePortalCleanup';
import { t } from '../lib/i18n';
import { LayoutDashboard, MessageCircle, TrendingUp, Calendar, Clock, Settings, LogOut, ScanFace, Sun, Moon, Inbox } from 'lucide-react';
import { Separator } from './ui/separator';
import { BrandFooter } from './BrandFooter';
import { PrivateSeo } from './seo/PrivateSeo';

const navItems = [
  { key: 'dashboard', path: '/dashboard', icon: LayoutDashboard },
  { key: 'coach', path: '/coach', icon: MessageCircle },
  { key: 'simulation', path: '/simulation', icon: ScanFace },
  { key: 'progress', path: '/progress', icon: TrendingUp },
  { key: 'routine', path: '/routine', icon: Calendar },
  { key: 'history', path: '/history', icon: Clock },
  { key: 'settings', path: '/settings', icon: Settings },
];

// Only rendered for administrators (desktop sidebar); mobile reaches it from Settings.
const adminNavItem = { key: 'adminInbox', path: '/admin/support', icon: Inbox };

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logoutUser, user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const sidebarItems = user?.is_admin ? [...navItems, adminNavItem] : navItems;
  const { theme, toggleTheme } = useTheme();
  usePortalCleanup();

  const handleLogout = () => {
    logoutUser();
    navigate('/auth');
  };

  return (
    <div className="flex min-h-screen bg-[var(--lux-ink)]">
      <PrivateSeo />
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-[72px] bg-[var(--lux-obsidian)] border-r border-[var(--lux-border)] py-4 items-center gap-1 fixed h-full z-50">
        <div className="flex items-center justify-center w-10 h-10 mb-4">
          <img src="/logo.jpeg" alt="PrimeFace" className="w-10 h-10 rounded-xl object-cover border border-lux-gold/30" />
        </div>
        <Separator className="w-8 mb-2 opacity-20" />
        
        {sidebarItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          return (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => navigate(item.path)}
              title={t(item.key, language)}
              className={`flex items-center justify-center w-11 h-11 rounded-xl transition-colors duration-200 ${
                isActive
                  ? 'bg-lux-gold/15 text-[var(--lux-gold)]'
                  : 'text-[var(--lux-text-secondary)] hover:text-[var(--lux-text)] hover:bg-[var(--lux-card-hover)]'
              }`}
            >
              <Icon className="w-5 h-5" />
            </button>
          );
        })}

        <div className="flex-1" />

        <button
          data-testid="logout-button"
          onClick={handleLogout}
          title={t('logout', language)}
          className="flex items-center justify-center w-11 h-11 rounded-xl text-[var(--lux-text-muted)] hover:text-[var(--lux-danger)] hover:bg-[var(--lux-card-hover)] transition-colors duration-200 mb-2"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </aside>

      {/* Bottom Nav - Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-lux-obsidian/95 backdrop-blur-md border-t border-[var(--lux-border)] flex justify-around py-2 px-1">
        {navItems.slice(0, 6).map(item => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          return (
            <button
              key={item.key}
              data-testid={`mobile-nav-${item.key}`}
              onClick={() => navigate(item.path)}
              className={`flex flex-1 min-w-0 flex-col items-center gap-0.5 px-1 py-1 rounded-lg transition-colors duration-200 ${
                isActive ? 'text-[var(--lux-gold)]' : 'text-[var(--lux-text-muted)]'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] truncate max-w-full">{t(item.key, language)}</span>
            </button>
          );
        })}
      </nav>

      {/* Main Content */}
      <main className="flex-1 md:ml-[72px] pb-20 md:pb-0 min-h-screen">
        {/* Top Bar with Language Toggle */}
        <div className="sticky top-0 z-40 flex items-center justify-end gap-2 px-4 sm:px-6 py-2 bg-lux-ink/80 backdrop-blur-md border-b border-[var(--lux-border)]">
          <div className="flex items-center bg-[var(--lux-panel)] rounded-lg border border-[var(--lux-border)] p-0.5">
            <button
              data-testid="topbar-lang-fr"
              onClick={() => setLanguage('fr')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors duration-200 ${
                language === 'fr'
                  ? 'bg-[var(--lux-gold)] text-[var(--lux-ink)]'
                  : 'text-[var(--lux-text-secondary)] hover:text-[var(--lux-text)]'
              }`}
            >
              FR
            </button>
            <button
              data-testid="topbar-lang-en"
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors duration-200 ${
                language === 'en'
                  ? 'bg-[var(--lux-gold)] text-[var(--lux-ink)]'
                  : 'text-[var(--lux-text-secondary)] hover:text-[var(--lux-text)]'
              }`}
            >
              EN
            </button>
          </div>
          {/* Theme Toggle */}
          <button
            data-testid="topbar-theme-toggle"
            onClick={toggleTheme}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-[var(--lux-panel)] border border-[var(--lux-border)] text-[var(--lux-text-secondary)] hover:text-[var(--lux-gold)] transition-colors duration-200"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
        {children}
        <BrandFooter variant="layout" />
      </main>
    </div>
  );
}
